/**
 * Todos os campos de texto do prontuario. A IA retorna o schema completo com
 * `null` explicito nos campos nao mencionados na consulta (por instrucao do
 * prompt); um registro salvo assim tem essas colunas null no banco. Reabrir
 * esse registro para edicao alimenta `null` como valor inicial de um campo de
 * texto — e atribuir `null` ao `.value` de um input/textarea no DOM vira
 * literalmente o texto "null" na tela.
 */
const TEXT_FIELDS = new Set([
  "chief_complaint",
  "history",
  "behavior",
  "diet",
  "environment",
  "mucous_membranes",
  "capillary_refill_time",
  "hydration_status",
  "lymph_nodes",
  "cardiac_auscultation",
  "respiratory_auscultation",
  "abdominal_palpation",
  "locomotor_system",
  "skin_coat",
  "other_systems",
  "presumptive_diagnosis",
  "definitive_diagnosis",
  "treatment",
  "follow_up_notes",
  "death_cause",
]);

/** Troca `null` por `""` nos campos de texto conhecidos, preservando tudo o mais. */
export const nullifyTextFieldsToEmptyString = <T extends object>(values: Partial<T>): Partial<T> => {
  const result: Record<string, unknown> = { ...values };

  for (const field of TEXT_FIELDS) {
    if (result[field] === null) {
      result[field] = "";
    }
  }

  return result as Partial<T>;
};

const APPENDABLE_TEXT_FIELDS = new Set([
  "chief_complaint",
  "history",
  "behavior",
  "diet",
  "environment",
  "mucous_membranes",
  "capillary_refill_time",
  "hydration_status",
  "lymph_nodes",
  "cardiac_auscultation",
  "respiratory_auscultation",
  "abdominal_palpation",
  "locomotor_system",
  "skin_coat",
  "other_systems",
  "presumptive_diagnosis",
  "definitive_diagnosis",
  "treatment",
  "follow_up_notes",
]);

const normalizeComparableText = (value: string) =>
  value
    .trim()
    .replace(/\r\n/g, "\n")
    .replace(/\n{3,}/g, "\n\n");

const appendTextIfMissing = (currentValue: string, newValue: string) => {
  const nextChunk = normalizeComparableText(newValue);
  if (!nextChunk) return currentValue;

  const normalizedCurrent = normalizeComparableText(currentValue);
  if (normalizedCurrent.includes(nextChunk)) return currentValue;
  if (!normalizedCurrent) return newValue;

  return `${currentValue}\n\n${newValue}`;
};

const isEmptyValue = (value: unknown) =>
  value === undefined ||
  value === null ||
  (typeof value === "string" && value.trim().length === 0);

export const mergeMedicalRecordValues = <T extends object>(
  currentValues: T,
  incomingValues: Partial<T>,
): T => {
  const merged = { ...currentValues } as T;
  const current = currentValues as Record<string, unknown>;
  const incoming = incomingValues as Record<string, unknown>;
  const result = merged as Record<string, unknown>;

  for (const [field, newValue] of Object.entries(incoming)) {
    if (isEmptyValue(newValue)) continue;

    const currentValue = current[field];
    if (
      APPENDABLE_TEXT_FIELDS.has(field) &&
      typeof newValue === "string" &&
      (isEmptyValue(currentValue) || typeof currentValue === "string")
    ) {
      result[field] = appendTextIfMissing(
        typeof currentValue === "string" ? currentValue : "",
        newValue,
      );
      continue;
    }

    if (isEmptyValue(currentValue)) {
      result[field] = newValue;
    }
  }

  return merged;
};
